import { type ReactElement, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBuildDetailQuery, useDeleteBuild } from '@/features/builds';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { usePipelineDetailQuery } from '@/features/pipelines';
import { useProjectFilter } from '@/features/projects';
import { useEntityDetailPageScaffold } from '@/features/shared/hooks/useEntityDetailPageScaffold';
import { DetailPageScaffold } from '@/ui/composites/scaffolds';
import { BuildDetailContent } from './BuildDetailContent';
import type { AuthSession } from '@/auth/types';

type BuildDetailPageProps = { session: AuthSession | null };

export const BuildDetailPage = ({ session }: BuildDetailPageProps): ReactElement => {
  const { pipelineSlug, runNumber } = useParams<{ pipelineSlug: string; runNumber: string }>();
  const { selectedOrganisationId } = useOrganisationFilter();
  const navigate = useNavigate();
  const deleteBuild = useDeleteBuild();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const resolvedPipelineSlug = useMemo(() => (pipelineSlug ?? '').trim(), [pipelineSlug]);
  const parsedRunNumber = useMemo(() => {
    const parsed = Number(runNumber);
    return Number.isFinite(parsed) ? parsed : null;
  }, [runNumber]);
  const pipelineQuery = usePipelineDetailQuery(resolvedPipelineSlug || null);
  const resolvedPipelineId = pipelineQuery.data?.id ?? null;
  const buildQuery = useBuildDetailQuery(
    resolvedPipelineId,
    parsedRunNumber,
    selectedProjectId ?? null,
  );
  const { data: build } = buildQuery;
  const isPending = pipelineQuery.isPending || buildQuery.isPending;
  const isError = pipelineQuery.isError || buildQuery.isError;
  const error = pipelineQuery.error ?? buildQuery.error;
  const hasInvalidParams = !resolvedPipelineSlug || parsedRunNumber === null;
  const missingPipeline =
    !pipelineQuery.isPending && pipelineQuery.isFetched && !pipelineQuery.data;
  const missingBuild =
    !hasInvalidParams && !missingPipeline && !isPending && buildQuery.isFetched && !build;
  const detailPage = useEntityDetailPageScaffold({
    load: {
      data: build,
      isPending,
      isFetched: buildQuery.isFetched,
      hasInvalidParams,
      additionalNotFound: missingPipeline || missingBuild,
    },
    delete: {
      mutation: deleteBuild,
      successMessage: 'Build deleted.',
      fallbackErrorMessage: 'Failed to delete build.',
    },
    redirects: {
      onDeleteTo: '/pipelines',
      notFoundBackLabel: 'Back to pipelines',
    },
    notFoundCopy: {
      title: 'Build not found',
      body: 'This build does not exist or you no longer have access to it.',
    },
  });

  useEffect(() => {
    if (!build?.projectId) return;
    if (selectedProjectId && build.projectId !== selectedProjectId) {
      void navigate('/pipelines', { replace: true });
      return;
    }
    if (!selectedProjectId) {
      setSelectedProjectId(build.projectId);
    }
  }, [build, navigate, selectedProjectId, setSelectedProjectId]);

  if (detailPage.showNotFound) return detailPage.renderNotFound();

  return (
    <DetailPageScaffold
      session={session}
      sessionMessage="Sign in to view build details."
      organisationId={selectedOrganisationId}
      requireProject={false}
      projectMessage="Select a project to view build details."
      projectId={selectedProjectId}
      isPending={isPending}
      isError={isError}
      error={error}
      errorMessage="Failed to load build"
      data={build}
    >
      {(resolvedBuild) => {
        return (
          <BuildDetailContent
            build={resolvedBuild}
            onDelete={detailPage.handleDelete}
            deleteLoading={detailPage.deleteLoading}
          />
        );
      }}
    </DetailPageScaffold>
  );
};
